import type { JSXElementConstructor, ReactElement, ReactNode } from 'react';
import React, { cloneElement, useEffect, useLayoutEffect, useState } from 'react';
import { Header } from './Header/Header';
import { UnauthorizedHeader } from './Header/UnauthorizedHeader';
import Footer from './Footer/Footer';
import { LayoutContextProvider } from '../../utils/customHooks/LayoutContext';
import ActiveAlarmsModal from '../ActiveAlarmsModal/ActiveAlarmsModal';
import { LayoutContainer, MainWrapper, SMain } from './Layout.styles';

type ILayoutProps = {
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  isMessageLayout: boolean;
  message: string;
  noTranslate?: boolean;
  to?: string;
  backBtn?: boolean;
  isAuthLayout?: boolean;
  showCoSafeLogo?: boolean;
  showBottomTabs?: boolean;
  subMessageText?: string;
  subMessageType?: 'default' | 'accent';
  showDots?: boolean;
  dotsCallBack?: () => void;
  handleHeaderContentClick?: () => void;
  headerBackgroundColor?: string | null;
  className?: string | undefined;
  backBtnCallBack?: (e: any) => void;
  backgroundColor?: string | undefined;
  isAlarmCreateLayout?: boolean;
  renderCustomRightButton?: () => ReactNode;
  showLanguageSelector?: boolean;
  showHeader?: boolean;
  showTenantSwitcher?: boolean;
};

export const Layout = (props: ILayoutProps) => {
  const {
    children,
    message,
    isMessageLayout,
    to,
    backBtn,
    isAuthLayout = true,
    showCoSafeLogo = false,
    showBottomTabs = false,
    noTranslate = false,
    subMessageText,
    subMessageType,
    showDots = false,
    dotsCallBack,
    handleHeaderContentClick,
    headerBackgroundColor,
    className,
    backBtnCallBack,
    backgroundColor,
    isAlarmCreateLayout = false,
    renderCustomRightButton,
    showLanguageSelector,
    showHeader = true,
    showTenantSwitcher = false,
  } = props;

  const [tabsState, setTabsState] = useState<boolean>(showBottomTabs);
  const [backLink, setBackLink] = useState<string | undefined>(to);
  const [title, setTitle] = useState<string | undefined>(message);
  const [subTitle, setSubTitle] = useState<string | undefined>(subMessageText);
  const [doShowDots, setDoShowDots] = useState<boolean | undefined>(showDots);
  const [headerMenu, setHeaderMenu] = useState<boolean>(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [headerState, setHeaderState] = useState<boolean>(showHeader);
  const [belowHeaderContent, setBelowHeaderContent] = useState<ReactNode>();

  useEffect(() => {
    if (to) setBackLink(to);
  }, [to]);

  useLayoutEffect(() => {
    setTitle(message);
  }, [message]);

  useLayoutEffect(() => {
    setSubTitle(subMessageText);
  }, [subMessageText]);
  useLayoutEffect(() => {
    setDoShowDots(showDots);
  }, [showDots]);

  const handleDotsMenu = () => {
    setHeaderMenu(!headerMenu);
    dotsCallBack?.();
  };

  return (
    <LayoutContainer>
      {!isAuthLayout && headerState && (
        <UnauthorizedHeader
          to={to ?? backLink}
          showLanguageSelector={showLanguageSelector}
          backBtn={backBtn}
        />
      )}
      {isAuthLayout && headerState && (
        <Header
          backBtnCallBack={backBtnCallBack}
          to={backLink ?? to}
          isMessageLayout={isMessageLayout}
          message={title ?? message}
          noTranslate={noTranslate}
          backBtn={backBtn}
          showCoSafeLogo={showCoSafeLogo}
          subMessageText={subMessageText ?? subTitle}
          subMessageType={subMessageType}
          showDots={doShowDots ?? showDots}
          dotsCallBack={handleDotsMenu}
          handleHeaderContentClick={handleHeaderContentClick}
          backgroundColor={headerBackgroundColor}
          renderCustomRightButton={renderCustomRightButton}
          showTenantSwitcher={showTenantSwitcher}
          belowHeaderContent={belowHeaderContent}
        />
      )}
      <LayoutContextProvider
        setTabsState={setTabsState}
        setBackLink={setBackLink}
        setMessage={setTitle}
        setSubTitle={setSubTitle}
        setDoShowDots={setDoShowDots}
        setHeaderMenu={setHeaderMenu}
        setShowHeader={setHeaderState}
        isMenuOpen={headerMenu}
        isMapOpen={mapOpen}
        setMapOpen={setMapOpen}
        setBelowHeaderContent={setBelowHeaderContent}
        currentTitle={title ?? ''}
      >
        <MainWrapper>
          <SMain
            className={className}
            extraFooterPadding={tabsState}
            backgroundColor={backgroundColor}
          >
            <>{cloneElement(children, { setShowBottomTabs: setTabsState })}</>
          </SMain>
        </MainWrapper>
      </LayoutContextProvider>
      {<Footer showBottomTabs={tabsState} />}
      {isAlarmCreateLayout && <ActiveAlarmsModal />}
    </LayoutContainer>
  );
};

Layout.defaultProps = {
  isMessageLayout: false,
  message: null,
};
